export type LandingLang = 'en' | 'ru';

export const landingTranslations = {
  en: {
    // hero
    heroTitle: 'Decentralized AI Platform',
    heroSubtitle: 'Distributed Inference, GPU-powered, Agents Store',
    getAccess: 'Get Access',
    login: 'Log in',

    // about
    aboutSwarMind: 'About Swarmind',
    aboutSwarMindDesc1:
      'We are building a decentralized AI platform that simplifies deploying and scaling inference models and GPU infrastructure for businesses and developers.',
    aboutSwarMindDesc2:
      'Our mission is to make powerful AI technologies accessible, transparent, and cost-efficient for building and monetizing AI solutions.',
    quickStart: 'One API for All AI',
    quickStartDesc:
      'OpenAI-compatible API. Dozens of popular models for all types of tasks, responsive technical support.',
    documentation: 'Documentation',
    infrastructureSavings: 'GPU Server Rental',
    infrastructureSavingsDesc: 'Rent Bare-metal GPU at a favorable price and pay only for actual usage.',

    // possibilities
    morePossibilities: 'More possibilities',
    aiCreators: 'AI Creators',
    aiCreatorsDesc1:
      'Publish your AI agents on our platform and make them available to a wide audience of users without managing infrastructure or payments.',
    aiCreatorsDesc2Before: 'You keep up to ',
    aiCreatorsDesc2Strong: '90% of the revenue',
    aiCreatorsDesc2After: ', while we handle hosting, scaling, and distribution.',
    gpuProviders: 'GPU Providers',
    gpuProvidersDesc1:
      'Connect your GPUs to our platform and monetize idle computing resources without having to find clients yourself.',
    gpuProvidersDesc2Before:
      'Machines can be launched quickly, and our decentralized architecture allows operation without strict time constraints. We take only a ',
    gpuProvidersDesc2Strong: '10% commission',
    gpuProvidersDesc2After: '.',

    // models
    popularModels: 'Popular Models',
    input: 'INPUT:',
    output: 'OUTPUT:',
    price: 'PRICE:',
    per1MTokens: 'per 1M tokens',
    perImage: 'per image',
    chat: 'chat',

    // footer
    swarmind: 'Swarmind',
    footerDescription: 'Decentralized AI-Platform. Distributed Inference, GPU-powered, Agents Store.',
    dataPolicy: 'Data Policy',
    userAgreement: 'User Agreement',
    language: 'Language',
    theme: 'Theme',
    lightMode: 'Light Mode',

    // prototype nav + misc
    learnMore: 'Learn more',
    tokens: '/1K tokens',
    homeNav: 'Home',
    unifiedAPINav: 'Unified API',
    gpuRentalNav: 'GPU Rental',
    freeInfrastructureNav: 'For Developers',
    unifiedAPITitle: 'One API for All AI',
    unifiedAPISubtitle:
      'Use AI capabilities to the maximum with OpenAI-compatible Swarmind API that provides access to dozens of models and ready-made tools.',
    unifiedAPIFeaturesTitle: 'Fast, simple and scalable AI model inference',
    unifiedAPIFeature1Title: 'Fast start',
    unifiedAPIFeature1Desc:
      'Integrate any model or solution into your projects using an OpenAI-compatible API. Documentation.',
    unifiedAPIFeature2Title: 'Low cost.',
    unifiedAPIFeature2Desc:
      'Pay only for actual usage at the lowest price achieved through resource decentralization.',
    unifiedAPIFeature3Title: 'Instant scalability.',
    unifiedAPIFeature3Desc:
      'A wide network of GPUs that connect as load increases. Your service will withstand activity spikes without performance loss.',
    unifiedAPIModelsTitle: 'All popular neural networks and our own AI solutions store',
    unifiedAPIWhyTitle: 'Why choose Swarmind for AI access',
    unifiedAPIWhy1Title: 'Stable infrastructure and transparency.',
    unifiedAPIWhy1Desc:
      'We use our own blockchain for node authorization, storing performance metrics and response times. Based on this data, a rating system operates: nodes with low uptime or unacceptable delays are automatically excluded from the network, which ensures stable service operation.',
    unifiedAPIWhy2Title: 'Fair cost.',
    unifiedAPIWhy2Desc:
      'We use a transparent and predictable billing model without hidden fees and surcharges. The platform charges a single commission of 10% either from the cost of GPU resources or from the price set by the author of the AI tool or model. No additional fees or coefficients.',
    unifiedAPIWhy3Title: 'Without bureaucracy and unnecessary restrictions.',
    unifiedAPIWhy3Desc:
      'We operate without excessive bureaucracy, manual approvals, and artificial restrictions. The platform has no regional blockages or censorship filters. You get access to neural networks when and where you need it, without unnecessary barriers and delays.',
    unifiedAPIHowToTitle: 'How to start working with Swarmind',
    unifiedAPIHowTo1Title: 'Get access.',
    unifiedAPIHowTo1Desc: 'Leave a request, a manager will contact you shortly and grant access.',
    unifiedAPIHowTo2Title: 'Use API to access models.',
    unifiedAPIHowTo2Desc: 'Use an API key or web interface to access models.',
    unifiedAPIHowTo3Title: 'Track work.',
    unifiedAPIHowTo3Desc: 'Evaluate all benefits during the free period.',
    unifiedAPIHowTo4Title: 'Pay as you go.',
    unifiedAPIHowTo4Desc: 'Top up your account in a convenient way, with a card or cryptocurrency.',

    // gpu rental page (prototype)
    gpuRentalTitle: 'Dedicated GPU Server Rental',
    gpuRentalSubtitle: 'Launch your server with the required GPU capacity at the best price',
    gpuRentalTasksTitle: 'For solving which tasks are GPU servers suitable',
    gpuRentalTask1Title: 'Machine Learning',
    gpuRentalTask1Desc:
      'Use a GPU server for training and fine-tuning models, experimenting with architectures, processing large datasets, and running computationally intensive ML pipelines. Full control over the environment and resources without capital expenditures on infrastructure.',
    gpuRentalTask2Title: 'Generative Models',
    gpuRentalTask2Desc:
      'Create images, video, and audio using modern generative AI models on GPU servers. Save time by using ready-made presets with popular models.',
    gpuRentalTask3Title: 'Large Language Models',
    gpuRentalTask3Desc:
      'Run LLMs with Hugging Face for text generation, code, and analytics on high-performance GPUs. Fine-tune and use them in your own projects without the need to maintain infrastructure.',
    gpuRentalTask4Title: 'Data Analysis',
    gpuRentalTask4Desc:
      'Processing terabytes of data from CRM, DWH, or logs with accelerated computations on GPU servers allows obtaining results practically in real-time, quickly identifying patterns, and making decisions based on up-to-date information.',
    gpuRentalWhyTitle: 'Why you should rent a GPU server on Swarmind',
    gpuRentalWhy1Title: 'Stable infrastructure and transparency.',
    gpuRentalWhy1Desc:
      'We use our own blockchain for node authorization, storing performance metrics and response times. Based on this data, a rating system operates: nodes with low uptime or unacceptable delays are automatically excluded from the network, which ensures stable service operation.',
    gpuRentalWhy2Title: 'Fair cost.',
    gpuRentalWhy2Desc:
      'We use a transparent and predictable billing model without hidden fees and surcharges. The platform charges a single commission of 10% of the cost of GPU resources. No additional fees or coefficients.',
    gpuRentalWhy3Title: 'Without bureaucracy and unnecessary restrictions.',
    gpuRentalWhy3Desc:
      'We operate without excessive bureaucracy, manual approvals, and artificial restrictions. The platform has no regional blocks or censorship filters. You get access to GPU servers when and where you need them, without unnecessary barriers and delays.',
    gpuRentalHowToTitle: 'How to rent a GPU server on Swarmind',
    gpuRentalHowTo1Title: 'Get access.',
    gpuRentalHowTo1Desc:
      'Leave a request, in the shortest possible time a manager will contact you and grant access.',
    gpuRentalHowTo2Title: 'Choose a suitable server with GPU.',
    gpuRentalHowTo2Desc: 'You can rent bare-metal GPU or choose one of the presets.',
    gpuRentalHowTo3Title: 'Pay as you go.',
    gpuRentalHowTo3Desc:
      'Top up your account in a convenient way, by card or cryptocurrency to pay for the server.',

    // free infrastructure page (prototype)
    freeInfrastructureTitle: 'Free Infrastructure for AI Developers',
    freeInfrastructureSubtitle:
      'Publish your solutions on our platform and make them available to a wide audience',
    freeInfrastructureFocusTitle: "Focus on development, we'll take care of everything else",
    freeInfrastructureFocus1Title: 'Global audience',
    freeInfrastructureFocus1Desc:
      'No need to think about marketing. Your project will be available to all Swarmind users. We are interested in promotion and will make an announcement on our resources.',
    freeInfrastructureFocus2Title: 'Infrastructure savings',
    freeInfrastructureFocus2Desc:
      'A wide network of GPUs that connect as the load grows. Your project will withstand spikes in activity without loss of performance.',
    freeInfrastructureFocus3Title: 'Transparent monetization',
    freeInfrastructureFocus3Desc:
      'Platform commission 10%, no hidden fees, bureaucracy, freezing of funds. Detailed usage statistics in your personal account.',
    freeInfrastructureProjectsTitle: 'What projects are we interested in',
    freeInfrastructureProject1Title: 'Generative',
    freeInfrastructureProject1Desc:
      'Tools for content creation — text, images, video or audio. Your AI helps users generate ideas and materials instantly, increasing productivity and creativity.',
    freeInfrastructureProject2Title: 'Applied',
    freeInfrastructureProject2Desc:
      'Solutions for specific tasks — analytics, forecasting, data processing. Your AI helps businesses and users quickly get results and make decisions based on up-to-date information.',
    freeInfrastructureProject3Title: 'Tools for developers',
    freeInfrastructureProject3Desc:
      'AI tools for developers that help analyze code, generate documentation, automate repetitive tasks, and speed up workflows.',
    freeInfrastructureHowToTitle: 'How to place a project on Swarmind',
    freeInfrastructureHowTo1Title: 'Get access.',
    freeInfrastructureHowTo1Desc:
      'Leave a request, and a manager will contact you shortly to discuss the project.',
    freeInfrastructureHowTo2Title: 'Add a project.',
    freeInfrastructureHowTo2Desc:
      "Add your project or model to Swarmind following the manager's instructions.",
    freeInfrastructureHowTo3Title: 'Track work.',
    freeInfrastructureHowTo3Desc:
      'Your personal account displays detailed statistics on the use of your solution.',
    freeInfrastructureHowTo4Title: 'Withdraw income.',
    freeInfrastructureHowTo4Desc: 'Withdraw funds received from users without delays.',

    // faq / qa
    faqTitle: 'Frequently asked questions',
    faqQ1: 'What is Swarmind?',
    faqA1:
      'Swarmind is a decentralised platform which provides access to custom large language models hosted on private machines.',
    faqQ2: 'What services does Swarmind provide?',
    faqA2:
      'First and foremost, we provide access to various LLMs, including text models, image generation, text-to-speech and image recognition. Secondly, we provide authors of models with a way to monetize their work via our decentralised payment system. Same goes for hardware suppliers.',
    faqQ3: 'How does our payment system work?',
    faqA3:
      "We're a decentralised platform, so we incorporate web3 to manage payments: in exchange for USDT you get access to any custom model that suits your needs. Of course, we also provide you with free tokens so you can just play around before moving forward.",
    faqQ4: 'How to use the service?',
    faqA4: 'Just pick your desired model in the chat window and chat with it.',
    faqQ5: 'How to connect my GPU?',
    faqA5:
      "Currently we connect hardware to cluster manually. Just contact us via Telegram and we'll set you up!",
    faqQ6: 'How to apply my LLM?',
    faqA6: 'For a detailed walkthrough and help, contact us via Telegram!',

    // form / modal
    formName: 'Your Name',
    formContact: 'Email | Telegram',
    formType: 'Request Type',
    formTypeSelect: 'Request Type',
    formTypeRentGpu: 'Rent GPU',
    formTypeRentOutGpu: 'Rent Out GPU',
    formTypeAgentPlacement: 'Agent Placement',
    formTypeLlmBusiness: 'LLM for business',
    formTypeLlmPersonal: 'LLM for personal use',
    formTargetGpuModel: 'Target GPU Model',
    formGpuCount: 'How Many GPUs Do You Need?',
    formMinVram: 'Minimum VRAM (GB)',
    formExpectedUptime: 'Expected Uptime',
    formGpuModel: 'GPU Model',
    formGpuCountOut: 'How Many GPU',
    formTotalVram: 'Total VRAM',
    formAvailableUptime: 'Available Uptime',
    formModelType: 'Model Type',
    formModelName: 'Model Name',
    formModelUrl: 'Model URL',
    formAvgInputTokens: 'Avg Input Token Quantity (daily)',
    formAvgOutputTokens: 'Avg Output Token Quantity (daily)',
    formAgentDescription: 'Agent Description',
    formAgentDescriptionPlaceholder: 'Please describe the agent you want to place',
    close: 'Close',
    cancel: 'Cancel',
    submitting: 'Submitting...',
    successTitle: 'Thanks — request received.',
    successText: "We'll contact you shortly to provide access.",

    // validation
    errNameRequired: 'Your name is required.',
    errContactRequired: 'Email or Telegram is required.',
    errTypeRequired: 'Please select a request type.',
  },
  ru: {
    // hero
    heroTitle: 'Децентрализованная AI-Платформа',
    heroSubtitle: 'Распределенный Инференс, GPU-мощности, Магазин Агентов',
    getAccess: 'получить доступ',
    login: 'войти',

    // about
    aboutSwarMind: 'О Swarmind',
    aboutSwarMindDesc1:
      'Мы создаем децентрализованную AI-платформу, которая упрощает развертывание и масштабирование моделей инференса и GPU-инфраструктуры для бизнеса и разработчиков.',
    aboutSwarMindDesc2:
      'Наша миссия — сделать мощные AI-технологии доступными, прозрачными и экономически эффективными для создания и монетизации AI-решений.',
    quickStart: 'One API for All AI',
    quickStartDesc:
      'OpenAI-совместимый API. Десятки популярных моделей для всех типов задач, оперативная тех-поддержка.',
    documentation: 'Документация',
    infrastructureSavings: 'GPU Server Rental',
    infrastructureSavingsDesc: 'Арендуйте Bare-metal GPU по выгодной цене и платите только за фактическое использование.',

    // possibilities
    morePossibilities: 'Больше возможностей',
    aiCreators: 'AI-Создатели',
    aiCreatorsDesc1:
      'Публикуйте своих AI-агентов на нашей платформе и делайте их доступными широкой аудитории пользователей без управления инфраструктурой или платежами.',
    aiCreatorsDesc2Before: 'Вы получаете до ',
    aiCreatorsDesc2Strong: '90% дохода',
    aiCreatorsDesc2After: ', а мы занимаемся хостингом, масштабированием и распространением.',
    gpuProviders: 'GPU-Провайдеры',
    gpuProvidersDesc1:
      'Подключайте свои GPU к нашей платформе и монетизируйте простаивающие вычислительные ресурсы без необходимости самостоятельно искать клиентов.',
    gpuProvidersDesc2Before:
      'Машины можно запускать быстро, а наша децентрализованная архитектура позволяет работать без строгих временных ограничений. Мы берем только ',
    gpuProvidersDesc2Strong: '10% комиссии',
    gpuProvidersDesc2After: '.',

    // models
    popularModels: 'Популярные модели',
    input: 'ВХОД:',
    output: 'ВЫХОД:',
    price: 'ЦЕНА:',
    per1MTokens: 'за 1M токенов',
    perImage: 'за картинку',
    chat: 'чат',

    // footer
    swarmind: 'Swarmind',
    footerDescription: 'Децентрализованная AI-Платформа. Распределенный Инференс, GPU-мощности, Магазин Агентов.',
    dataPolicy: 'Политика данных',
    userAgreement: 'Пользовательское соглашение',
    language: 'Язык',
    theme: 'Тема',
    lightMode: 'Светлая тема',

    // prototype nav + misc
    learnMore: 'Узнать больше',
    tokens: '/1K токенов',
    homeNav: 'Главная',
    unifiedAPINav: 'Одно API для всех AI',
    gpuRentalNav: 'Аренда GPU серверов',
    freeInfrastructureNav: 'Для разработчиков',
    unifiedAPITitle: 'Одно API для всех AI',
    unifiedAPISubtitle:
      'Используйте возможности AI по максимуму с OpenAI-совместимым API Swarmind, который предоставляет доступ к десяткам моделей и готовым инструментам.',
    unifiedAPIFeaturesTitle: 'Быстрый, простой и масштабируемый вывод AI моделей',
    unifiedAPIFeature1Title: 'Быстрый старт',
    unifiedAPIFeature1Desc:
      'Внедряйте в свои проекты любую модель или решение с помощью OpenAI совместимого API. Документация.',
    unifiedAPIFeature2Title: 'Низкая стоимость.',
    unifiedAPIFeature2Desc:
      'Платите только за фактическое использование по самой низкой цене достигнутой за счет децентрализации ресурсов.',
    unifiedAPIFeature3Title: 'Моментальная масштабируемость.',
    unifiedAPIFeature3Desc:
      'Широкая сеть GPU, которые подключаются по мере роста нагрузки. Ваш сервис выдержит всплески активности без потери производительности.',
    unifiedAPIModelsTitle: 'Все популярные нейронные сети и наш магазин AI решений',
    unifiedAPIWhyTitle: 'Почему стоит выбрать Swarmind для доступа к AI',
    unifiedAPIWhy1Title: 'Стабильная инфраструктура и прозрачность.',
    unifiedAPIWhy1Desc:
      'Мы используем собственный блокчейн для авторизации узлов, хранения метрик производительности и времени отклика. На основе этих данных работает рейтинговая система: узлы с низким аптаймом или недопустимыми задержками автоматически исключаются из сети, что обеспечивает стабильную работу сервиса.',
    unifiedAPIWhy2Title: 'Справедливая стоимость.',
    unifiedAPIWhy2Desc:
      'Мы используем прозрачную и предсказуемую модель биллинга без скрытых платежей и надбавок. Платформа взимает единую комиссию 10% — либо с стоимости GPU-ресурсов, либо с цены, установленной автором AI-инструмента или модели. Никаких дополнительных сборов и коэффициентов.',
    unifiedAPIWhy3Title: 'Без бюрократии и лишних ограничений.',
    unifiedAPIWhy3Desc:
      'Работаем без избыточной бюрократии, ручных согласований и искусственных ограничений. На платформе нет региональных блокировок и цензурных фильтров. Вы получаете доступ к нейросетям тогда и там, где это нужно, без лишних барьеров и задержек.',
    unifiedAPIHowToTitle: 'Как начать работу с Swarmind',
    unifiedAPIHowTo1Title: 'Получите доступ.',
    unifiedAPIHowTo1Desc:
      'Оставьте заявку, в кротчайшие сроки с вами свяжется менеджер и выдаст доступ.',
    unifiedAPIHowTo2Title: 'Используйте API для доступа к моделям.',
    unifiedAPIHowTo2Desc: 'Используйте API ключ или веб-интерфейс для доступа к моделям.',
    unifiedAPIHowTo3Title: 'Отслеживайте работу.',
    unifiedAPIHowTo3Desc: 'Оцените все преимущества на бесплатном периоде.',
    unifiedAPIHowTo4Title: 'Платите по факту.',
    unifiedAPIHowTo4Desc: 'Пополняйте счет удобным способом, картой или криптовалютой',

    // gpu rental page (prototype)
    gpuRentalTitle: 'Аренда выделенных серверов с GPU',
    gpuRentalSubtitle: 'Запустите свой сервер с необходимым объемом GPU по самой выгодной цене',
    gpuRentalTasksTitle: 'Для решения каких задач подходят GPU сервера',
    gpuRentalTask1Title: 'Машинное обучение',
    gpuRentalTask1Desc:
      'Используйте сервер с GPU для обучения и дообучения моделей, экспериментов с архитектурами, обработки больших датасетов и запуска вычислительно-ёмких ML-пайплайнов. Полный контроль над окружением и ресурсами без капитальных затрат на инфраструктуру.',
    gpuRentalTask2Title: 'Генеративные модели',
    gpuRentalTask2Desc:
      'Создавайте изображения, видео и аудио с помощью современных генеративных AI моделей на серверах с GPU. Экономьте время используя готовые пресеты с популярными моделями.',
    gpuRentalTask3Title: 'Большие языковые модели',
    gpuRentalTask3Desc:
      'Запускайте LLM с Hugging Face для генерации текста, кода и аналитики на производительных GPU. Дообучайте и используйте в собственных проектах без необходимости содержать инфраструктуру.',
    gpuRentalTask4Title: 'Анализ данных',
    gpuRentalTask4Desc:
      'Обработка терабайтов данных из CRM, DWH или логов с ускорением вычислений на серверах с GPU позволяет получать результаты практически в реальном времени, быстро выявлять закономерности и принимать решения на основе актуальной информации.',
    gpuRentalWhyTitle: 'Почему стоит арендовать сервер с GPU на Swarmind',
    gpuRentalWhy1Title: 'Стабильная инфраструктура и прозрачность.',
    gpuRentalWhy1Desc:
      'Мы используем собственный блокчейн для авторизации узлов, хранения метрик производительности и времени отклика. На основе этих данных работает рейтинговая система: узлы с низким аптаймом или недопустимыми задержками автоматически исключаются из сети, что обеспечивает стабильную работу сервиса.',
    gpuRentalWhy2Title: 'Справедливая стоимость.',
    gpuRentalWhy2Desc:
      'Мы используем прозрачную и предсказуемую модель биллинга без скрытых платежей и надбавок. Платформа взимает единую комиссию 10% с стоимости GPU-ресурсов. Никаких дополнительных сборов и коэффициентов.',
    gpuRentalWhy3Title: 'Без бюрократии и лишних ограничений.',
    gpuRentalWhy3Desc:
      'Работаем без избыточной бюрократии, ручных согласований и искусственных ограничений. На платформе нет региональных блокировок и цензурных фильтров. Вы получаете доступ к серверам с GPU тогда и там, где это нужно, без лишних барьеров и задержек.',
    gpuRentalHowToTitle: 'Как арендовать сервер с GPU на Swarmind',
    gpuRentalHowTo1Title: 'Получите доступ.',
    gpuRentalHowTo1Desc:
      'Оставьте заявку, в кротчайшие сроки с вами свяжется менеджер и выдаст доступ.',
    gpuRentalHowTo2Title: 'Выберете подходящий сервер c GPU.',
    gpuRentalHowTo2Desc: 'Вы можете арендовать как bare-metal GPU так и выбрать один из пресетов.',
    gpuRentalHowTo3Title: 'Платите по факту.',
    gpuRentalHowTo3Desc:
      'Пополняйте счет удобным способом, картой или криптовалютой для оплаты сервера.',

    // free infrastructure page (prototype)
    freeInfrastructureTitle: 'Бесплатная инфраструктура для AI разработчиков',
    freeInfrastructureSubtitle:
      'Публикуйте свои решения на нашей платформе и делайте их доступными широкой аудитории',
    freeInfrastructureFocusTitle: 'Сосредоточьтесь на разработке, все остальное мы возьмем на себя',
    freeInfrastructureFocus1Title: 'Глобальная аудитория',
    freeInfrastructureFocus1Desc:
      'Не нужно думать о маркетинге. Ваш проект будет доступен всем пользователям Swarmind. Мы заинтересованы в продвижении и сделаем анонс на своих ресурсах.',
    freeInfrastructureFocus2Title: 'Экономия на инфраструктуре',
    freeInfrastructureFocus2Desc:
      'Широкая сеть GPU, которые подключаются по мере роста нагрузки. Ваш проект выдержит всплески активности без потери производительности.',
    freeInfrastructureFocus3Title: 'Прозрачная монетизация',
    freeInfrastructureFocus3Desc:
      'Коммиссия платформы 10%, никаких скрытых комиссий, бюрократии, заморозки средств. Подробная статистика использования в личном кабинете.',
    freeInfrastructureProjectsTitle: 'Какие проекты нам интересны',
    freeInfrastructureProject1Title: 'Генеративные',
    freeInfrastructureProject1Desc:
      'Инструменты для создания контента — текста, изображений, видео или аудио. Ваш AI помогает пользователям генерировать идеи и материалы мгновенно, повышая продуктивность и креативность.',
    freeInfrastructureProject2Title: 'Прикладные',
    freeInfrastructureProject2Desc:
      'Решения для конкретных задач — аналитика, прогнозирование, обработка данных. Ваш AI помогает бизнесу и пользователям быстро получать результаты и принимать решения на основе актуальной информации.',
    freeInfrastructureProject3Title: 'Инструменты для разработчиков',
    freeInfrastructureProject3Desc:
      'AI-инструменты для разработчиков, которые помогают анализировать код, генерировать документацию, автоматизировать повторяющиеся задачи и ускорять рабочие процессы.',
    freeInfrastructureHowToTitle: 'Как разместить проект на Swarmind',
    freeInfrastructureHowTo1Title: 'Получите доступ.',
    freeInfrastructureHowTo1Desc:
      'Оставьте заявку, в кротчайшие сроки с вами свяжется менеджер для обсуждения проекта.',
    freeInfrastructureHowTo2Title: 'Добавьте проект.',
    freeInfrastructureHowTo2Desc:
      'Добавьте ваш проект или модель на Swarmind действуя по инструкции менеджера.',
    freeInfrastructureHowTo3Title: 'Отслеживайте работу.',
    freeInfrastructureHowTo3Desc:
      'В личном кабинете отражена подробная статистика использования вашего решения.',
    freeInfrastructureHowTo4Title: 'Выводите доход.',
    freeInfrastructureHowTo4Desc: 'Выводите полученные от пользователей средства без задержек.',

    // faq / qa
    faqTitle: 'Часто задаваемые вопросы',
    faqQ1: 'Что такое Swarmind?',
    faqA1:
      'Swarmind — это децентрализованная платформа, которая предоставляет доступ к кастомным большим языковым моделям, размещённым на частных машинах.',
    faqQ2: 'Какие сервисы предоставляет Swarmind?',
    faqA2:
      'В первую очередь мы предоставляем доступ к различным LLM: текстовым моделям, генерации изображений, text-to-speech и распознаванию изображений. Во вторую — даём авторам моделей способ монетизировать свою работу через нашу децентрализованную платёжную систему. То же самое — для поставщиков железа.',
    faqQ3: 'Как работает платёжная система?',
    faqA3:
      'Мы — децентрализованная платформа, поэтому используем web3 для управления платежами: в обмен на USDT вы получаете доступ к любой кастомной модели, которая подходит под вашу задачу. Также мы даём бесплатные токены, чтобы вы могли попробовать сервис перед тем, как продолжать.',
    faqQ4: 'Как пользоваться сервисом?',
    faqA4: 'Просто выберите нужную модель в окне чата и общайтесь с ней.',
    faqQ5: 'Как подключить свою GPU?',
    faqA5:
      'Сейчас мы подключаем железо к кластеру вручную. Просто напишите нам в Telegram — и мы всё настроим!',
    faqQ6: 'Как разместить свою LLM?',
    faqA6: 'Для подробной инструкции и помощи напишите нам в Telegram!',

    // form / modal
    formName: 'Ваше имя',
    formContact: 'Email | Telegram',
    formType: 'Тип запроса',
    formTypeSelect: 'Тип запроса',
    formTypeRentGpu: 'Аренда GPU',
    formTypeRentOutGpu: 'Сдача GPU в аренду',
    formTypeAgentPlacement: 'Размещение агента',
    formTypeLlmBusiness: 'LLM для бизнеса',
    formTypeLlmPersonal: 'LLM для личного использования',
    formTargetGpuModel: 'Модель GPU',
    formGpuCount: 'Сколько GPU вам нужно?',
    formMinVram: 'Минимальный VRAM (GB)',
    formExpectedUptime: 'Ожидаемое время работы',
    formGpuModel: 'Модель GPU',
    formGpuCountOut: 'Количество GPU',
    formTotalVram: 'Общий VRAM',
    formAvailableUptime: 'Доступное время работы',
    formModelType: 'Тип модели',
    formModelName: 'Название модели',
    formModelUrl: 'URL модели',
    formAvgInputTokens: 'Среднее количество входных токенов (в день)',
    formAvgOutputTokens: 'Среднее количество выходных токенов (в день)',
    formAgentDescription: 'Описание агента',
    formAgentDescriptionPlaceholder: 'Опишите агента, которого вы хотите разместить',
    close: 'Закрыть',
    cancel: 'Отмена',
    submitting: 'Отправка...',
    successTitle: 'Спасибо — запрос получен.',
    successText: 'В ближайшее время мы свяжемся с вами и предоставим доступ.',

    // validation
    errNameRequired: 'Пожалуйста, укажите имя.',
    errContactRequired: 'Пожалуйста, укажите Email или Telegram.',
    errTypeRequired: 'Пожалуйста, выберите тип запроса.',
  },
} as const;

export type LandingTranslationKey = keyof typeof landingTranslations.en;

export function tLanding(lang: LandingLang, key: LandingTranslationKey): string {
  return landingTranslations[lang][key] ?? landingTranslations.en[key] ?? String(key);
}
